import React, { memo } from 'react';
import { useStyles } from 'uinix-ui';

import { Input } from '../../types/Input';
import { Nullable } from '../../types/Nullable';
import { Box } from '../Box/Box';

type Props = Input<number> & {
  /** Whether to allow decimal values */
  allowDecimals?: boolean;
  /** Supported UI modes */
  mode?: 'digits';
  /** Max value allowed */
  max?: Nullable<number>;
  /** Min value allowed */
  min?: Nullable<number>;
  /** HTML `step` attribute */
  step?: number | 'any';
};

export const NumberInput = memo(
  ({
    allowDecimals = true,
    error,
    mode,
    onChange,
    step,
    value,
    width,
    ...rest
  }: Props) => {
    const styles = useStyles();
    const effectiveStep = step ?? (allowDecimals ? 'any' : 1);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const valueString = event.target.value;
      if (event.target.checkValidity()) {
        const numericValue = valueString === '' ? null : Number(valueString);

        if (
          !allowDecimals &&
          numericValue !== null &&
          !Number.isInteger(numericValue)
        ) {
          return;
        }

        onChange(numericValue);
      }
    };

    const handleFocus = (focusEvent: React.FocusEvent<HTMLInputElement>) => {
      focusEvent.target.addEventListener(
        'wheel',
        (wheelEvent: Event) => wheelEvent.preventDefault(),
        { passive: false },
      );
    };

    return (
      <Box
        {...rest}
        as="input"
        step={effectiveStep}
        onFocus={handleFocus}
        styles={[
          styles.input.container,
          mode === 'digits' ? componentStyles.digitsWidth : null,
        ]}
        styleProps={{ error, value, width }}
        type="number"
        value={value === null ? '' : value}
        onChange={handleChange}
      />
    );
  },
);

const componentStyles = {
  digitsWidth: ({ value }: { value: Nullable<number> }) => ({
    minWidth: 'unset',
    paddingRight: 0,
    width: `calc(${(value ?? 1).toString().length}ch + 36px)`,
  }),
};
